"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { LoaderCircle, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authCopy } from "@/features/auth/content/auth-copy";
import { requestMagicLink } from "@/features/auth/services/auth-service";
import { useAppLocale } from "@/features/localization/app-locale-provider";
import { ApiError } from "@/lib/api";

type MagicLinkFormValues = { email: string };
interface MagicLinkFormProps { tenantCode: string; }
const RESEND_COOLDOWN_SECONDS = 60;

export function MagicLinkForm({ tenantCode }: Readonly<MagicLinkFormProps>) {
  const { locale } = useAppLocale();
  const copy = authCopy[locale];
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const validationRef = useRef(copy.signIn);
  useEffect(() => {
    validationRef.current = copy.signIn;
  }, [copy.signIn]);
  const { formState: { errors, isSubmitting }, handleSubmit, register, reset, setError, trigger } = useForm<MagicLinkFormValues>({
    resolver: (values, context, options) => {
      const messages = validationRef.current;
      return zodResolver(
        z.object({
          email: z.string().trim().min(1, messages.emailRequired).email(messages.emailInvalid),
        }),
      )(values, context, options);
    },
    mode: "onChange",
    defaultValues: { email: "" },
  });

  useEffect(() => {
    const fieldsWithErrors = Object.keys(errors) as (keyof MagicLinkFormValues)[];
    if (fieldsWithErrors.length > 0) void trigger(fieldsWithErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function submitRequest(email: string): Promise<boolean> {
    setRequestError(null);
    try {
      await requestMagicLink({ email, tenant: tenantCode, locale });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        setError("email", { message: copy.signIn.emailInvalid });
      } else if (error instanceof ApiError && error.status === 429) {
        setRequestError(copy.signIn.errors.rateLimited);
      } else {
        setRequestError(copy.signIn.errors.unavailable);
      }
      return false;
    }
  }

  async function onSubmit(values: MagicLinkFormValues): Promise<void> {
    const email = values.email.trim().toLowerCase();
    if (await submitRequest(email)) {
      setSubmittedEmail(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStatus(null);
    }
  }

  async function resend(): Promise<void> {
    if (!submittedEmail || cooldown > 0 || isResending) return;
    setIsResending(true);
    setStatus(null);
    if (await submitRequest(submittedEmail)) {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStatus(copy.signIn.confirmation.resent);
    }
    setIsResending(false);
  }

  function changeEmail(): void {
    reset({ email: submittedEmail ?? "" });
    setSubmittedEmail(null);
    setCooldown(0);
    setRequestError(null);
    setStatus(null);
  }

  if (submittedEmail) {
    return (
      <section aria-labelledby="magic-link-confirmation-title" className="space-y-4 text-center">
        <div className="space-y-2">
          <h2 id="magic-link-confirmation-title" className="text-xl font-semibold text-card-foreground">{copy.signIn.confirmation.title}</h2>
          <p className="text-sm text-muted-foreground">{copy.signIn.confirmation.sentTo}</p>
          <p className="break-all font-medium text-card-foreground">{submittedEmail}</p>
        </div>
        <ol className="list-inside list-decimal space-y-2 text-left text-sm text-muted-foreground">
          <li>{copy.signIn.confirmation.firstStep}</li>
          <li>{copy.signIn.confirmation.secondStep}</li>
        </ol>
        <Button className="h-[52px] w-full rounded-xl 2xl:h-14" disabled={cooldown > 0 || isResending} onClick={resend} type="button">
          {cooldown > 0 ? copy.signIn.confirmation.resendIn(cooldown) : copy.signIn.confirmation.resend}
        </Button>
        <Button className="h-[52px] w-full rounded-xl 2xl:h-14" onClick={changeEmail} type="button" variant="ghost">{copy.signIn.confirmation.changeEmail}</Button>
        <div aria-live="polite" role="status" className="text-sm text-muted-foreground">{status}</div>
        {requestError ? <Alert aria-live="assertive" role="alert"><AlertDescription>{requestError}</AlertDescription></Alert> : null}
      </section>
    );
  }

  return (
    <form aria-busy={isSubmitting} className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-2">
        <Label className="sr-only" htmlFor="magic-link-email">{copy.signIn.emailLabel}</Label>
        <div className="relative">
          <Mail aria-hidden="true" className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground 2xl:size-5" />
          <Input
            id="magic-link-email"
            className="h-[52px] rounded-xl bg-card pr-4 pl-12 text-base shadow-xs placeholder:text-muted-foreground md:text-base 2xl:h-14"
            type="email"
            autoComplete="email"
            aria-describedby={errors.email ? "magic-link-email-error" : undefined}
            aria-invalid={Boolean(errors.email)}
            disabled={isSubmitting}
            placeholder={copy.signIn.emailLabel}
            {...register("email")}
          />
        </div>
        {errors.email ? <p id="magic-link-email-error" className="text-sm text-destructive">{errors.email.message}</p> : null}
      </div>
      <Button className="h-[52px] w-full rounded-xl text-sm font-semibold shadow-sm 2xl:h-14 2xl:text-base" disabled={isSubmitting} type="submit">
        {isSubmitting
          ? <span key="busy" aria-live="polite" className="inline-flex items-center gap-1.5"><LoaderCircle aria-hidden="true" className="animate-spin" />{copy.signIn.submitting}</span>
          : <span key="idle" aria-live="polite">{copy.signIn.submit}</span>}
      </Button>
      <p className="mx-auto max-w-xs text-center text-sm leading-5 text-muted-foreground">{copy.signIn.microcopy}</p>
      {requestError ? <Alert aria-live="assertive" role="alert"><AlertDescription>{requestError}</AlertDescription></Alert> : null}
    </form>
  );
}
